import { Box, Typography } from '@mui/material';
import { headerStyles, theme, headerPadding } from '../constants/styles';
import { CountdownTimer } from './CountdownTimer';
import { useCountdown } from '../hooks/useCountdown';
import type { ThemeCustomization } from '../types';

export interface EventHeaderProps {
    /**
     * Data limite para inscrições no evento
     * Se não fornecido, o contador não será exibido
     */
    registrationDeadline?: Date | null;
    /**
     * URL do logo (opcional, usa padrão se não fornecido)
     */
    logoUrl?: string;
    /**
     * Personalização de tema do evento
     */
    themeCustomization?: ThemeCustomization;
}

/**
 * Componente de cabeçalho para páginas de eventos públicos
 * Exibe logo da Aviva e contador regressivo para deadline de inscrições
 * 
 * @example
 * <EventHeader registrationDeadline={new Date('2024-12-31')} />
 */
export function EventHeader({
    registrationDeadline,
    logoUrl = '/assets/logo/aviva_ingressos_horizontal.png',
    themeCustomization
}: EventHeaderProps) {
    const timeLeft = useCountdown(registrationDeadline);
    
    // Aplica gradiente personalizado se fornecido
    const gradientStart = themeCustomization?.colors?.gradient?.start || theme.colors.gradient.start;
    const gradientMiddle = themeCustomization?.colors?.gradient?.middle || theme.colors.gradient.middle;
    const gradientEnd = themeCustomization?.colors?.gradient?.end || theme.colors.gradient.end;
    const gradientBackground = `linear-gradient(90deg, ${gradientStart} 0%, ${gradientMiddle} 60%, ${gradientEnd} 100%)`;

    return (
        <Box sx={headerStyles.container}>
            {/* Painel branco à esquerda com logo */}
            <Box sx={headerStyles.logoPanel}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        component="img"
                        src={logoUrl}
                        alt="Aviva Ingressos"
                        sx={{
                            width: { xs: 160, sm: 180, md: 220 },
                            height: { xs: 25, sm: 28, md: 48 },
                            objectFit: 'contain',
                            marginLeft: { xs: 0, md: 4 },
                            maxWidth: '100%',
                        }}
                    />
                </Box>
            </Box>

            {/* Seção com gradiente e contador */}
            <Box
                sx={{
                    ...headerStyles.gradientPanel,
                    background: gradientBackground,
                    pl: {
                        xs: '210px',
                        sm: '290px',
                        md: headerPadding.desktop.md,
                        lg: headerPadding.desktop.lg,
                        xl: headerPadding.desktop.xl,
                    },
                }}
            >
                {/* Contador centralizado */}
                {timeLeft !== null && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'center', sm: 'center' },
                            justifyContent: { xs: 'center', sm: 'center' },
                            gap: { xs: 0.75, sm: 1, md: 1.5 },
                            width: { xs: '100%', sm: 'auto' },
                            maxWidth: '100%',
                            overflow: 'visible',
                            px: { xs: 0.5, sm: 1, md: 0 },
                            py: { xs: 0.5, sm: 0 },
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                                color: 'white',
                                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem', lg: '1.2rem' },
                                fontFamily: theme.fonts.primary,
                                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                textAlign: { xs: 'center', sm: 'left' },
                                lineHeight: 1.2,
                            }}
                        >
                            As inscrições encerram em
                        </Typography>
                        <CountdownTimer timeLeft={timeLeft} />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

